/**
 * Open-redirect guard for the post-login `next` destination.
 *
 * A `next` value only survives if it is unambiguously a *same-origin relative
 * path*. Anything that could steer the browser to another origin — a
 * protocol-relative URL (`//evil.com`), a backslash variant (`/\evil.com`), an
 * absolute URL with a scheme (`https://evil.com`, `javascript:...`), or a value
 * carrying control characters / whitespace — is rejected.
 *
 * @returns the sanitised relative path (starting with a single `/`, keeping its
 *   query string and hash), or `null` when the caller must fall back to a safe
 *   default such as the org home.
 */

// Control chars (U+0000-U+001F), space (U+0020), DEL (U+007F) and any other
// Unicode whitespace. Built from an ASCII-only source string so no literal
// control byte lives in this file.
// eslint-disable-next-line no-control-regex
const CONTROL_OR_WHITESPACE = new RegExp('[\\u0000-\\u0020\\u007f]|\\s')

export function safeNextPath(next: string | null | undefined): string | null {
  if (typeof next !== 'string' || next.length === 0) {
    return null
  }

  // Reject anything containing whitespace or control characters. Browsers strip
  // or specially handle tabs/newlines inside URLs, which attackers use to smuggle
  // a scheme past naive checks (e.g. a tab-injected "javascript:").
  if (CONTROL_OR_WHITESPACE.test(next)) {
    return null
  }

  // Must be an absolute, same-origin path: a single leading slash.
  if (!next.startsWith('/')) {
    return null
  }

  // Reject protocol-relative ("//host") and backslash-smuggling ("/\host",
  // which some browsers normalise to "//host").
  if (next[1] === '/' || next[1] === '\\') {
    return null
  }

  // Reject an embedded scheme in the first path segment. "/foo:bar" is a valid
  // path, but a value like "/http:evil" or "/javascript:alert(1)" should not be
  // honoured. Only guard the segment before the first `/`, `?` or `#`; a colon
  // later in the path or query string is legitimate.
  const firstSegment = next.slice(1).split(/[/?#]/, 1)[0]
  if (firstSegment.includes(':')) {
    return null
  }

  return next
}

// Backend-served pages (the gated API documentation, spec 2026-10-20-34): the
// SPA router has no route for them, so they need a full page load.
const SERVER_PAGE = /^\/(api\/|openapi\.yaml|llms\.txt|docs_old\.html)/

export function isServerPage(path: string): boolean {
  return SERVER_PAGE.test(path)
}
